import React from 'react';

/**
 * Shared shimmer primitive — animates with pulse effect
 */
export const Shimmer = ({ className }: { className: string }) => (
  <div className={`animate-pulse rounded bg-gray-200 ${className}`} />
);

/**
 * NoPlanSkeleton — mirrors NoPlanStatus card layout exactly.
 * Used when there are no plans to show during a refresh.
 */
export const NoPlanSkeleton = () => (
  <div className="my-4 animate-pulse rounded-lg border border-neutralLight bg-white p-4">
    <div className="flex items-center space-x-3">
      <div className="h-10 w-10 shrink-0 rounded-lg bg-gray-200" />
      <div className="h-4 w-3/4 rounded bg-gray-200" />
    </div>
  </div>
);

/**
 * PlanSectionSkeleton — mirrors PlanSection + VersionItem layout exactly.
 * Each skeleton card = one PlanSection with one VersionItem row.
 */
export const PlanSectionSkeleton = ({ count = 1 }: { count?: number }) => (
  <div
    className="space-y-4"
    aria-hidden
  >
    {Array.from({ length: count }).map((_, idx) => (
      <div
        key={idx}
        className="my-2 animate-pulse rounded-lg bg-white p-4 shadow-sm md:p-6"
      >
        {/* Header — matches PlanSection: title h2 + subtitle p + empty rounded-full button */}
        <div className="mb-4 flex items-start justify-between md:mb-6">
          <div>
            <Shimmer className="mb-2 h-5 w-48" />
            <Shimmer className="h-4 w-72" />
          </div>
          <div className="h-8 w-8 rounded-full" />
        </div>

        {/* Version list — matches space-y-3 rounded-xl border */}
        <div className="space-y-3 rounded-xl border">
          {/* VersionItem — relative m-2, inner row lg:h-[76px] */}
          <div className="relative m-2 flex h-auto flex-col rounded-lg">
            <div className="flex flex-col items-stretch rounded-lg md:flex-row md:items-center md:gap-0 lg:h-[76px]">
              {/* Left: file info — matches bg-btnBgColor (neutral gray) */}
              <div className="flex w-full flex-col gap-3 rounded-lg bg-gray-100 p-3 md:flex-1 md:flex-row md:items-center md:space-x-3 md:p-4 lg:h-full">
                {/* FileIcon wrapper: flex h-8 w-12 shrink-0 rounded-lg md:h-12 */}
                <Shimmer className="h-8 w-12 shrink-0 rounded-lg md:h-12 md:w-10" />
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <Shimmer className="h-4 w-40" />
                    <Shimmer className="h-5 w-16 rounded" />
                  </div>
                  <Shimmer className="mt-1 h-3 w-36" />
                </div>
                {/* ChevronRight — hidden on mobile */}
                <div className="hidden md:flex">
                  <Shimmer className="h-6 w-6" />
                </div>
              </div>

              {/* Right: approve + reject buttons — h-10 w-10 each */}
              <div className="mx-3 flex shrink-0 items-center justify-end space-x-3 px-3 pb-3 md:px-2 md:pb-2 lg:px-0 lg:pb-0">
                <Shimmer className="h-10 w-10 rounded" />
                <Shimmer className="h-10 w-10 rounded border" />
              </div>
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

/**
 * OperationsClosureSkeleton — matches OperationsClosure layout
 * Dashed upload box + one file card stub
 */
export const OperationsClosureSkeleton = () => (
  <div
    className="rounded-lg bg-white p-4 shadow-sm md:p-6"
    aria-hidden
  >
    {/* Title */}
    <Shimmer className="mb-4 h-6 w-56 md:mb-6" />

    {/* Dashed upload box */}
    <div className="mb-3 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Shimmer className="h-10 w-8 shrink-0" />
          <Shimmer className="h-4 w-72" />
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <Shimmer className="h-9 w-28 rounded-lg" />
          <Shimmer className="h-9 w-24 rounded-lg" />
        </div>
      </div>
    </div>

    {/* File card stub */}
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Shimmer className="h-10 w-8 shrink-0" />
          <div>
            <Shimmer className="mb-1.5 h-4 w-56" />
            <Shimmer className="h-3 w-40" />
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Shimmer className="h-9 w-28 rounded-lg" />
          <Shimmer className="h-9 w-24 rounded-lg" />
        </div>
      </div>
    </div>
  </div>
);

export type TargetsSkeletonPhase = 'initial' | 'processing' | 'error' | 'completed';

interface TargetsConfigSkeletonProps {
  /** Which SeasonTargets phase the real screen will land in (derived from upload history). */
  phase?: TargetsSkeletonPhase;
  /** Number of rows in the "File upload status" history card; 0 hides the card like the real tab. */
  historyCount?: number;
}

/**
 * Template row shared by every dashed phase card — mirrors TemplateActionsSection:
 * icon h-8 w-8 + text-sm line (20px) on the left, CustomButton (h-11 / lg:h-12) on the right.
 */
const TargetsActionRowSkeleton = ({ showDownload }: { showDownload: boolean }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <div className="flex items-center space-x-3 sm:space-x-4">
      <Shimmer className="h-8 w-8 shrink-0" />
      <Shimmer className="h-4 w-64 max-w-full" />
    </div>
    <div className="flex shrink-0 items-center space-x-3">
      {showDownload && <Shimmer className="h-11 w-36 rounded-lg lg:h-12" />}
      <Shimmer className="h-11 w-28 rounded-lg lg:h-12" />
    </div>
  </div>
);

/**
 * TargetsConfigSkeleton — mirrors SeasonTargets (inside renderTargetsTab's `my-4` wrapper):
 * title, target-type dropdown (label + field), the phase card, and the upload-history card.
 */
export const TargetsConfigSkeleton = ({
  phase = 'initial',
  historyCount = 0,
}: TargetsConfigSkeletonProps) => {
  const dashedCard = 'rounded-lg border-2 border-dashed border-gray-300 bg-white p-4 sm:p-6';

  return (
    <div className="my-4">
      <div
        className="rounded-lg p-4 md:p-6"
        aria-hidden
      >
        {/* Title — h2 text-lg md:text-xl (28px line) mb-4 md:mb-6 */}
        <div className="mb-4 flex h-7 items-center md:mb-6">
          <Shimmer className="h-5 w-36" />
        </div>

        {/* Dropdown — label (bodySm, 22.4px) + mb-1 + h-11/lg:h-12 field + mb-8, max-w-md */}
        <div className="flex w-full flex-col gap-3 sm:max-w-md sm:flex-row sm:items-center sm:gap-4">
          <div className="relative mb-8 w-full">
            <div className="mb-1 flex h-[22.4px] items-center">
              <Shimmer className="h-3.5 w-52" />
            </div>
            <Shimmer className="h-11 w-full rounded-xl lg:h-12" />
          </div>
        </div>

        {/* Phase card */}
        {phase === 'initial' && (
          <div className={dashedCard}>
            <TargetsActionRowSkeleton showDownload />
          </div>
        )}

        {phase === 'completed' && (
          <div className="space-y-4">
            <div className={dashedCard}>
              <TargetsActionRowSkeleton showDownload={false} />
            </div>
          </div>
        )}

        {(phase === 'processing' || phase === 'error') && (
          <div className={dashedCard}>
            <div className="space-y-4">
              <TargetsActionRowSkeleton showDownload />
              {/* Processing: space-y-3 > relative rounded-lg bg-white p-4. Error: bordered row. */}
              <div className="space-y-3">
                <div
                  className={`rounded-lg p-4 ${phase === 'error' ? 'border border-gray-200' : ''}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center space-x-3">
                      <Shimmer className="h-8 w-8 shrink-0" />
                      <div>
                        {/* name: text-sm (20px line) / meta: mt-1 + text-xs (16px line) */}
                        <div className="flex h-5 items-center">
                          <Shimmer className="h-4 w-44" />
                        </div>
                        <div className="mt-1 flex h-4 items-center">
                          <Shimmer className="h-3 w-52" />
                        </div>
                      </div>
                    </div>
                    {phase === 'error' && (
                      <Shimmer className="h-[34px] w-24 shrink-0 rounded-full" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* File upload status — mt-6 md:mt-8, border-2 p-4 md:p-6; hidden when there is no history */}
        {historyCount > 0 && (
          <div className="mt-6 rounded-lg border-2 border-gray-200 bg-white p-4 md:mt-8 md:p-6">
            {/* h3 text-lg font-semibold (28px line) mb-4 */}
            <div className="mb-4 flex h-7 items-center">
              <Shimmer className="h-5 w-36" />
            </div>

            <div className="space-y-3">
              {Array.from({ length: historyCount }).map((_, i) => (
                <div
                  key={i}
                  className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4"
                >
                  {/* Left: h-8 w-8 icon box holding the h-11 w-8 CSV icon, then name/date */}
                  <div className="flex items-center space-x-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded">
                      <Shimmer className="h-11 w-8 shrink-0" />
                    </div>
                    <div>
                      {/* Row 1: name (text-sm) + "Latest" badge (12px + py-1.5 = 24px) on the first row */}
                      <div className={`flex items-center gap-4 ${i === 0 ? 'h-6' : 'h-5'}`}>
                        <Shimmer className="h-4 w-40" />
                        {i === 0 && <Shimmer className="h-6 w-14 rounded-md" />}
                      </div>
                      {/* Row 2: bodyXs date (12px * 1.4) */}
                      <div className="flex h-[16.8px] items-center">
                        <Shimmer className="h-3 w-36" />
                      </div>
                    </div>
                  </div>

                  {/* Right: status badge (py-1 + 16.8px) + Download button (py-1 + border) */}
                  <div className="flex shrink-0 items-center space-x-3">
                    <Shimmer className="h-[25px] w-16 rounded-md" />
                    <Shimmer className="h-[27px] w-24 rounded" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
